import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Sparkles, Lightbulb, ListChecks, BookOpen, Mic, RotateCcw, FolderPlus } from "lucide-react";
import { api, API } from "../lib/api";
import { ResultCard } from "../components/ResultCard";
import { useLang } from "../context/LanguageContext";
import { track } from "../lib/analytics";
import { getGuestId } from "../lib/guest";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const DEMO_LINE = "Based on DEMO sample data — not live market records.";

export default function Ask() {
  const { t, dir } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const lastUserText = useRef("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [panel, setPanel] = useState({ suggestions: t("home.prompts"), sources: [], project: null });
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const lastReq = useRef(null);
  const started = messages.length > 0;
  const voiceSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => { if (location.state?.prompt) send(location.state.prompt); /* eslint-disable-next-line */ }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, loading]);

  const setLast = useCallback((patch) => {
    setMessages((m) => {
      const c = [...m];
      const idx = c.length - 1;
      if (idx < 0 || c[idx].role !== "assistant") return c;
      c[idx] = { ...c[idx], ...(typeof patch === "function" ? patch(c[idx]) : patch) };
      return c;
    });
  }, []);

  const handleEvent = useCallback((ev) => {
    switch (ev.type) {
      case "status": setLast({ status: ev.text }); break;
      case "card": setLast((cur) => ({ cards: [...(cur.cards || []), ev.card] })); break;
      case "citation": setPanel((p) => ({ ...p, sources: [...(p.sources || []), ev.source] })); break;
      case "message_start": setLast({ status: null, text: "" }); break;
      case "token": setLast((cur) => ({ text: (cur.text || "") + ev.text })); break;
      case "done":
        setLast({ streaming: false, status: null, followups: ev.suggested_actions || [], intent: ev.intent });
        setPanel((p) => ({
          ...p,
          suggestions: ev.suggested_actions?.length ? ev.suggested_actions : t("home.prompts"),
          project: ev.project_updates?.[ev.project_updates.length - 1] || p.project,
        }));
        break;
      case "error":
        setLast((cur) => ({ streaming: false, status: null, error: true, text: cur.text || ev.partial || "" }));
        break;
      default: break;
    }
  }, [setLast, t]);

  const stream = useCallback(async (msg, history) => {
    lastReq.current = { msg, history };
    setLoading(true);
    try {
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, history }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream unavailable");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try { handleEvent(JSON.parse(line.slice(5).trim())); } catch { /* ignore partial */ }
        }
      }
      setLast((cur) => (cur.streaming ? { streaming: false, status: null, error: !cur.text, text: cur.text || "" } : {}));
    } catch (e) {
      setLast((cur) => ({ streaming: false, status: null, error: true, text: cur.text || "" }));
    } finally {
      setLoading(false);
    }
  }, [handleEvent, setLast]);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    track("ask_prompt_submitted", { length: msg.length });
    lastUserText.current = msg;
    const history = messages.filter((m) => m.text).map((m) => ({ role: m.role, content: m.text }));
    setMessages((m) => [
      ...m,
      { role: "user", text: msg },
      { role: "assistant", text: "", cards: [], status: "Understanding your request…", streaming: true },
    ]);
    stream(msg, history);
  };

  const retry = () => {
    if (!lastReq.current || loading) return;
    setMessages((m) => {
      const c = [...m];
      if (c.length && c[c.length - 1].role === "assistant") c.pop();
      return [...c, { role: "assistant", text: "", cards: [], status: "Retrying…", streaming: true }];
    });
    stream(lastReq.current.msg, lastReq.current.history);
  };

  const trackProject = async () => {
    try {
      const { data } = await api.post("/projects", {
        goal: lastUserText.current || "Buy property in Dubai",
        goal_type: "property", guest_id: getGuestId(),
      });
      track("project_created", { source: "ask" });
      toast.success("Project created");
      navigate(`/projects/${data.id}`);
    } catch { toast.error("Could not create the project."); }
  };

  const toggleVoice = () => {
    if (!voiceSupported) { toast.info(t("ask.voice_unsupported")); return; }
    if (listening) { recognitionRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = dir === "rtl" ? "ar-AE" : "en-US";
    rec.interimResults = true;
    rec.onresult = (e) => {
      const txt = Array.from(e.results).map((r) => r[0].transcript).join("");
      setInput(txt);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8" data-testid="ask-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 flex flex-col">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-gold/80 flex items-center gap-2"><Sparkles size={14} /> {t("ask.eyebrow")}</div>
            <h1 className="font-display text-3xl text-white mt-1">{t("ask.title")}</h1>
            <div className="mt-2 inline-flex items-center gap-2 text-[11px] px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold/90" data-testid="ask-demo-disclaimer">
              {t("common.demo_strong")}
            </div>
          </div>

          <div className="flex-1 min-h-[52vh] space-y-5">
            {!started && !loading && (
              <div className="glass rounded-2xl p-8">
                <p className="text-white/70">{t("ask.intro")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {t("home.prompts").map((p) => (
                    <button key={p} onClick={() => send(p)} data-testid="starter-prompt"
                      className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-gold/40 hover:text-gold transition-colors duration-200">{p}</button>
                  ))}
                </div>
              </div>
            )}

            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className={m.role === "user" ? "flex justify-end" : ""}>
                  {m.role === "user" ? (
                    <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-gold text-ink px-4 py-2.5 text-sm font-medium" data-testid="user-message">{m.text}</div>
                  ) : (
                    <div className="space-y-3" data-testid="assistant-message">
                      {/* Typing indicator / agent status */}
                      {m.streaming && !m.text && (
                        <div className="glass rounded-2xl rounded-bl-sm px-5 py-4 flex items-center gap-3" data-testid="ai-thinking">
                          <span className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "140ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "280ms" }} />
                          </span>
                          <span className="text-gold/80 text-sm" data-testid="ai-status">{m.status || t("ask.thinking")}</span>
                        </div>
                      )}

                      {/* Cards stream in progressively */}
                      {m.cards?.length > 0 && (
                        <div className="grid sm:grid-cols-2 gap-3">{m.cards.map((c, k) => <ResultCard key={k} card={c} i={k} />)}</div>
                      )}

                      {/* Answer text (token-streamed) */}
                      {m.text && (
                        <div className="glass rounded-2xl rounded-bl-sm px-5 py-4 text-white/85 text-sm leading-relaxed whitespace-pre-wrap" data-testid="assistant-text">
                          {m.text.replace(DEMO_LINE, "").trimEnd()}
                          {m.streaming && <span className="inline-block w-1.5 h-4 align-middle ml-0.5 bg-gold/70 animate-pulse" />}
                          {m.text.includes(DEMO_LINE) && (
                            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold/90" data-testid="message-demo-badge">
                              {DEMO_LINE}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error recovery */}
                      {m.error && (
                        <div className="flex items-center gap-3 text-sm text-rose-300" data-testid="ai-error">
                          <span>{m.text ? t("ask.partial") : t("ask.fail")}</span>
                          <button onClick={retry} data-testid="ai-retry"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gold/30 text-gold hover:bg-gold/10 transition-colors">
                            <RotateCcw size={13} /> {t("ask.retry")}
                          </button>
                        </div>
                      )}

                      {/* Follow-up chips + Track as Project after the answer */}
                      {!m.streaming && (m.followups?.length > 0 || m.intent === "property") && (
                        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="followup-chips">
                          {m.intent === "property" && (
                            <button onClick={trackProject} data-testid="track-as-project"
                              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">
                              <FolderPlus size={13} /> {t("ask.track_project")}
                            </button>
                          )}
                          {m.followups?.slice(0, 4).map((s) => (
                            <button key={s} onClick={() => send(s)} data-testid="followup-chip"
                              className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-gold/40 hover:text-gold transition-colors duration-200">{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={endRef} />
          </div>

          <div className="sticky bottom-4 mt-4">
            <div className="glass rounded-2xl p-2 flex items-end gap-2 focus-within:border-gold/40 transition-colors duration-300">
              {voiceSupported && (
                <button onClick={toggleVoice} data-testid="ask-voice-btn" aria-label="Voice input"
                  className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${listening ? "bg-rose-500/20 text-rose-300 animate-pulse" : "text-white/60 hover:text-gold"}`}>
                  <Mic size={18} />
                </button>
              )}
              <textarea data-testid="ask-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1}
                placeholder={listening ? t("ask.listening") : t("ask.placeholder")} className="flex-1 bg-transparent px-3 py-2.5 text-white placeholder:text-white/40 outline-none resize-none text-sm" />
              <button onClick={() => send()} disabled={loading} data-testid="ask-send-btn" aria-label="Send"
                className="shrink-0 w-10 h-10 rounded-xl bg-gold text-ink flex items-center justify-center hover:brightness-110 disabled:opacity-40 transition-all duration-200"><ArrowUp size={18} /></button>
            </div>
          </div>
        </div>

        <aside className="md:col-span-5 space-y-4 md:sticky md:top-20 self-start" data-testid="ask-side-panel">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 text-white/80 text-sm font-semibold mb-3"><ListChecks size={16} className="text-gold" /> {t("ask.panel_project")}</div>
            {panel.project ? (
              <div>
                <div className="text-white font-medium">{panel.project.goal}</div>
                <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{panel.project.status}</span>
                <ul className="mt-3 space-y-1.5">{(panel.project.next_actions || []).map((a) => <li key={a} className="text-xs text-white/60 flex gap-2"><span className="text-gold">›</span>{a}</li>)}</ul>
              </div>
            ) : <p className="text-xs text-white/50">{t("ask.empty_project")}</p>}
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 text-white/80 text-sm font-semibold mb-3"><Lightbulb size={16} className="text-gold" /> {t("ask.panel_follow")}</div>
            <div className="flex flex-wrap gap-2">
              {panel.suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="suggested-action"
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:border-gold/40 hover:text-gold transition-colors duration-200">{s}</button>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 text-white/80 text-sm font-semibold mb-3"><BookOpen size={16} className="text-gold" /> {t("ask.panel_sources")}</div>
            {panel.sources.length ? (
              <ul className="space-y-2">{panel.sources.map((s, i) => (
                <li key={i} className="text-xs text-white/55 border-l-2 border-gold/30 pl-3"><span className="text-gold">{s.badge}</span> · <span className="text-white/70">{s.tool}</span> — {s.note}</li>
              ))}</ul>
            ) : <p className="text-xs text-white/50">{t("ask.empty_sources")}</p>}
          </div>

          <WaitlistBox />
        </aside>
      </div>
    </div>
  );
}

const WaitlistBox = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/waitlist", { email, capability: "ask", company_website: hp });
      setDone(true);
      track("waitlist_joined", { source: "ask" });
      toast.success(t("ask.waitlist_ok"));
    } catch (err) {
      toast.error(t("common.error"));
    }
  };
  return (
    <div className="glass rounded-2xl p-5" data-testid="ask-waitlist">
      <div className="text-white/80 text-sm font-semibold">{t("ask.waitlist_h")}</div>
      <p className="text-xs text-white/50 mt-1">{t("ask.waitlist_p")}</p>
      {done ? (
        <p className="text-xs text-emerald-400 mt-3">{t("ask.waitlist_ok")}</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("ask.waitlist_ph")} data-testid="waitlist-email"
            className="flex-1 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
          <button type="submit" data-testid="waitlist-submit" className="px-3 py-2 rounded-lg bg-gold text-ink text-sm font-semibold hover:brightness-110 transition-all duration-200">{t("ask.waitlist_btn")}</button>
        </form>
      )}
    </div>
  );
};
